import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Bell, Star, User, CreditCard, MessageCircle, LogOut, ChevronLeft, ChevronRight, Users, Newspaper } from 'lucide-react';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';

const NAV = [
  {
    section: 'Newsletters',
    items: [
      { to: '/newsletters', icon: Newspaper, label: 'Choix newsletter' },
      { to: '/alertes',     icon: Bell,      label: 'Mes alertes' },
      { to: '/favoris',     icon: Star,      label: 'Favoris' },
    ],
  },
  {
    section: 'Mon espace',
    items: [
      { to: '/profil',      icon: User,        label: 'Mon profil' },
      { to: '/abonnement',  icon: CreditCard,  label: 'Abonnement' },
      { to: '/contact',     icon: MessageCircle, label: 'Contact' },
    ],
  },
];

const Sidebar: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const user = auth.currentUser;
  const isAdmin = user?.email === 'campantgabriel@gmail.com';

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/login');
  };

  return (
    <aside
      className="h-screen sticky top-0 flex flex-col border-r border-gray-100 transition-all duration-300 bg-white"
      style={{ width: collapsed ? 64 : 220, minWidth: collapsed ? 64 : 220 }}
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-gray-100">
        <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Aglomera" className="w-8 h-8 rounded-lg object-contain flex-shrink-0" style={{ background: '#e6edfa', padding: '4px' }} />
        {!collapsed && (
          <span className="font-bold text-base" style={{ color: '#2a52a8' }}>Aglomera</span>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-2">
        {NAV.map(group => (
          <div key={group.section} className="mb-6">
            {!collapsed && (
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 px-3 mb-2">
                {group.section}
              </p>
            )}
            {group.items.map(({ to, icon: Icon, label }) => (
              <NavLink
                key={to}
                to={to}
                id={to === '/favoris' ? 'favoris-nav-target' : undefined}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl mb-0.5 text-sm font-medium transition-all ${
                    isActive
                      ? 'text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`
                }
                style={({ isActive }) => isActive ? { background: '#2a52a8' } : {}}
                title={collapsed ? label : undefined}
              >
                <Icon size={18} className="flex-shrink-0" />
                {!collapsed && <span>{label}</span>}
              </NavLink>
            ))}
          </div>
        ))}
        {isAdmin && (
          <div className="mb-6">
            {!collapsed && (
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 px-3 mb-2">
                Admin
              </p>
            )}
            <NavLink
              to="/alertes-clients"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl mb-0.5 text-sm font-medium transition-all ${
                  isActive
                    ? 'text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`
              }
              style={({ isActive }) => isActive ? { background: '#2a52a8' } : {}}
              title={collapsed ? 'Alertes clients' : undefined}
            >
              <Users size={18} className="flex-shrink-0" />
              {!collapsed && <span>Alertes clients</span>}
            </NavLink>
          </div>
        )}
      </nav>

      {/* User + logout */}
      <div className="border-t border-gray-100 p-3">
        {!collapsed && user && (
          <div className="flex items-center gap-2 px-2 py-2 mb-1">
            <span className="text-xs text-gray-600 truncate">{user.email}</span>
          </div>
        )}
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-500 hover:bg-red-50 hover:text-red-600 transition-all w-full"
          title={collapsed ? 'Déconnexion' : undefined}
        >
          <LogOut size={18} className="flex-shrink-0" />
          {!collapsed && <span>Déconnexion</span>}
        </button>
      </div>

      {/* Collapse button */}
      <button
        onClick={() => setCollapsed(c => !c)}
        className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white border border-gray-200 flex items-center justify-center shadow-sm hover:bg-gray-50 transition-colors z-10"
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
};

export default Sidebar;
